import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import { HiOutlineDocumentCheck, HiOutlineShieldCheck } from "react-icons/hi2";
import { useAuth } from "@/auth/AuthContext";
import AccountService from "@/services/AccountService";
import { toaster } from "@/components/ui/toaster";
import { Field, FormTextarea } from "@/components/jobOffer/ui/inputs";
import SectionCard from "./SectionCard";

const empty = { gdprConsentText: "", privacyPolicyConsentText: "", privacyPolicyContent: "" };

const toForm = (data) => ({
    gdprConsentText: data?.gdprConsentText ?? "",
    privacyPolicyConsentText: data?.privacyPolicyConsentText ?? "",
    privacyPolicyContent: data?.privacyPolicyContent ?? "",
});

// Blank fields fall back to the chat's default texts.
const ChatConsentsSection = () => {
    const { t } = useTranslation("account");
    const { isOwner } = useAuth();

    const [saved, setSaved] = useState(empty);
    const [form, setForm] = useState(empty);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        AccountService.getChatConsents()
            .then((data) => {
                setSaved(toForm(data));
                setForm(toForm(data));
            })
            .catch(() => toaster.create({ type: "error", title: t("settingsPage.consents.toastLoadError") }))
            .finally(() => setIsLoading(false));
    }, []);

    const isDirty = JSON.stringify(form) !== JSON.stringify(saved);
    const disabled = !isOwner || isSaving;
    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const data = await AccountService.updateChatConsents(form);
            setSaved(toForm(data));
            setForm(toForm(data));
            toaster.create({ type: "success", title: t("settingsPage.consents.toastSaveSuccess") });
        } catch {
            toaster.create({ type: "error", title: t("settingsPage.consents.toastSaveError") });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <HStack w="full" py={8} justify="center">
                <Spinner color="brand.400" size="md" />
            </HStack>
        );
    }

    return (
        <VStack w="full" gap={4} align="flex-start">
            {!isOwner && (
                <Text color="text.grey" fontSize="sm">{t("settingsPage.consents.ownerOnly")}</Text>
            )}

            <SectionCard
                icon={<HiOutlineShieldCheck />}
                title={t("settingsPage.consents.checkboxesTitle")}
                subtitle={t("settingsPage.consents.checkboxesSubtitle")}
            >
                <Box mb={4}>
                    <Field label={t("settingsPage.consents.gdprLabel")} hint={t("settingsPage.consents.gdprHint")}>
                        <FormTextarea
                            rows={4}
                            value={form.gdprConsentText}
                            onChange={set("gdprConsentText")}
                            disabled={disabled}
                            maxLength={5000}
                            placeholder={t("settingsPage.consents.gdprPlaceholder")}
                        />
                    </Field>
                </Box>
                <Field label={t("settingsPage.consents.privacyLabel")} hint={t("settingsPage.consents.privacyHint")}>
                    <FormTextarea
                        rows={2}
                        value={form.privacyPolicyConsentText}
                        onChange={set("privacyPolicyConsentText")}
                        disabled={disabled}
                        maxLength={1000}
                        placeholder={t("settingsPage.consents.privacyPlaceholder")}
                    />
                </Field>
            </SectionCard>

            <SectionCard
                icon={<HiOutlineDocumentCheck />}
                title={t("settingsPage.consents.policyTitle")}
                subtitle={t("settingsPage.consents.policySubtitle")}
            >
                <Field label={t("settingsPage.consents.policyLabel")} hint={t("settingsPage.consents.policyHint")}>
                    <FormTextarea
                        rows={14}
                        resize="vertical"
                        value={form.privacyPolicyContent}
                        onChange={set("privacyPolicyContent")}
                        disabled={disabled}
                        maxLength={100000}
                        placeholder={t("settingsPage.consents.policyPlaceholder")}
                    />
                </Field>
            </SectionCard>

            {isOwner && (
                <HStack w="full" justify="flex-end" gap={2}>
                    <Button variant="outlined" size="sm" onClick={() => setForm(saved)} disabled={!isDirty || isSaving} type="button">
                        {t("settingsPage.cancelButton")}
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleSave} loading={isSaving} disabled={!isDirty} type="button">
                        {t("settingsPage.saveButton")}
                    </Button>
                </HStack>
            )}
        </VStack>
    );
};

export default ChatConsentsSection;
